/**
 * Disposition + editable case summary section, shared by the Summary view and the
 * Human Audit tab (rendered inside OutputBoardEditor).
 *
 * Self-contained wrapper around CaseDispositionAccordion: owns the disposition +
 * case-summary hooks/handlers so callers only supply the case + a single `canEdit`
 * gate. Renders nothing for workflows without a disposition engine.
 */
import type { CaseRecord } from '@compass/client/types/case_record';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { CaseDispositionAccordion } from '@/components/CaseDispositionAccordion';
import { DISPOSITION_WORKFLOW_TYPES } from '@/constants/dispositions';
import { useSelectableDispositions, useUpdateDispositionCode } from '@/hooks/useDispositions';
import { useCaseSummary, useUpdateSummary } from '@/hooks/useSummary';
import { useStore } from '@/store/index';
import { computeInputOutputChecksum } from '@/utils/checksum';
import { isSummaryEditableWorkflow } from '@/utils/workflow';

/** Workflows whose disposition engine backs the accordion. */
const DISPOSITION_WORKFLOWS = new Set<string>(DISPOSITION_WORKFLOW_TYPES);

interface DispositionSummarySectionProps {
  caseId: string;
  caseDetails: CaseRecord;
  /** Whether the user may edit disposition + summary (caller-computed gate). */
  canEdit: boolean;
}

export const DispositionSummarySection: React.FC<DispositionSummarySectionProps> = ({
  caseId,
  caseDetails,
  canEdit,
}) => {
  const workflowType = caseDetails?.workflow_type;
  const show = DISPOSITION_WORKFLOWS.has(workflowType ?? '');
  const user = useStore(state => state.user);

  // Selectable-only: retired codes are not offered, except the one this case is
  // already set to (so the control shows its current value instead of blank).
  const { data: dispositions = [], isLoading: isLoadingDispositions } = useSelectableDispositions(
    show ? workflowType : undefined,
    caseDetails?.disposition_code as number | undefined,
  );
  const updateDispositionCode = useUpdateDispositionCode(caseId);
  const { data: caseSummaryData } = useCaseSummary(caseId, !!caseId, { refetchInterval: 30000 });
  const updateSummary = useUpdateSummary(caseId);

  const [isEditingFullSummary, setIsEditingFullSummary] = useState(false);

  const dispositionOptions = dispositions.map(d => ({ value: d.code || 0, label: d.label || '' }));
  const currentDispositionCode = caseDetails?.disposition_code;
  const fullSummary = caseSummaryData?.summary?.content?.full_summary;

  const caseInputData = caseDetails?.input_data?.data;
  const caseOutputData = caseDetails?.output_data?.data;

  // Detect summary drift by comparing the stored checksum with the current data.
  const storedChecksum = caseSummaryData?.summary?.input_output_checksum;
  const [currentChecksum, setCurrentChecksum] = useState<string | null>(null);
  useEffect(() => {
    if (!storedChecksum || !caseInputData || !caseOutputData) {
      setCurrentChecksum(null);
      return;
    }
    void computeInputOutputChecksum(caseInputData, caseOutputData).then(setCurrentChecksum);
  }, [storedChecksum, caseInputData, caseOutputData]);
  const isSummaryStale = !!(
    storedChecksum &&
    currentChecksum &&
    storedChecksum !== currentChecksum
  );

  const handleDispositionChange = useCallback(
    (code: number | '') => {
      if (code !== '' && code !== currentDispositionCode) updateDispositionCode.mutate(code);
    },
    [currentDispositionCode, updateDispositionCode],
  );

  const handleFullSummarySave = useCallback(
    async (newSummary: string) => {
      const summaryId = caseSummaryData?.summary?.id;
      if (!summaryId) {
        toast.error('Summary ID not found');
        return;
      }
      const currentContent = caseSummaryData?.summary?.content || {};
      try {
        const newChecksum = await computeInputOutputChecksum(caseInputData, caseOutputData);
        await updateSummary.mutateAsync({
          summary_id: summaryId,
          content: { ...currentContent, full_summary: newSummary },
          edited_by: user?.email || 'user',
          input_output_checksum: newChecksum,
        });
        setIsEditingFullSummary(false);
      } catch {
        // useUpdateSummary surfaces the error toast; keep edit mode open.
      }
    },
    [caseSummaryData, updateSummary, user, caseInputData, caseOutputData],
  );

  if (!show) return null;

  const canEditFullSummary = canEdit && isSummaryEditableWorkflow(workflowType);

  return (
    <CaseDispositionAccordion
      currentDispositionCode={currentDispositionCode}
      dispositionOptions={dispositionOptions}
      fullSummary={fullSummary}
      isLoadingDispositions={isLoadingDispositions}
      isUpdating={updateDispositionCode.isPending}
      onDispositionChange={handleDispositionChange}
      expandedItems={['case-disposition']}
      onExpandedItemsChange={() => {}}
      canEdit={canEdit}
      canEditFullSummary={canEditFullSummary}
      isEditingFullSummary={isEditingFullSummary}
      onFullSummaryEdit={() => setIsEditingFullSummary(true)}
      onFullSummarySave={handleFullSummarySave}
      onFullSummaryCancel={() => setIsEditingFullSummary(false)}
      isLoadingFullSummary={updateSummary.isPending}
      isSummaryStale={isSummaryStale}
    />
  );
};
